import { ReactNode, useEffect, createContext, useCallback, useMemo } from 'react';
// hooks
import useLocalStorage from '../hooks/useLocalStorage';
// utils
import getColorPresets, { colorPresets, defaultPreset } from '../utils/getColorPresets';
// config
import { defaultSettings } from '../config';
// @type
import {
    ThemeMode,
    ThemeLayout,
    ThemeContrast,
    ThemeDirection,
    ThemeColorPresets,
    SettingsContextProps,
} from '../components/settings/type';

// ----------------------------------------------------------------------

const initialState: SettingsContextProps = {
    ...defaultSettings,
    // Mode
    onToggleMode: () => {},
    onChangeMode: () => {},

    // Direction
    onToggleDirection: () => {},
    onChangeDirection: () => {},
    onChangeDirectionByLang: () => {},

    // Layout
    onToggleLayout: () => {},
    onChangeLayout: () => {},

    // Contrast
    onToggleContrast: () => {},
    onChangeContrast: () => {},

    // Color
    onChangeColor: () => {},
    setColor: defaultPreset,
    colorOption: [],

    // Stretch
    onToggleStretch: () => {},

    // Reset
    onResetSetting: () => {},
};

const SettingsContext = createContext(initialState);

// ----------------------------------------------------------------------

type SettingsProviderProps = {
    children: ReactNode;
};

function SettingsProvider({ children }: SettingsProviderProps) {
    const [settings, setSettings] = useLocalStorage('settings', {
        themeMode: initialState.themeMode,
        themeLayout: initialState.themeLayout,
        themeStretch: initialState.themeStretch,
        themeContrast: initialState.themeContrast,
        themeDirection: initialState.themeDirection,
        themeColorPresets: initialState.themeColorPresets,
    });

    const isArabic = localStorage.getItem('i18nextLng') === 'ar';

    useEffect(() => {
        if (isArabic) {
            onChangeDirectionByLang('ar');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isArabic]);

    // Mode

    const onToggleMode = useCallback(() => {
        setSettings((prev) => ({
            ...prev,
            themeMode: prev.themeMode === 'light' ? 'dark' : 'light',
        }));
    }, [setSettings]);

    const onChangeMode = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSettings((prev) => ({
                ...prev,
                themeMode: (event.target as HTMLInputElement).value as ThemeMode,
            }));
        },
        [setSettings]
    );

    // Direction

    const onToggleDirection = useCallback(() => {
        setSettings((prev) => ({
            ...prev,
            themeDirection: prev.themeDirection === 'rtl' ? 'ltr' : 'rtl',
        }));
    }, [setSettings]);

    const onChangeDirection = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSettings((prev) => ({
                ...prev,
                themeDirection: (event.target as HTMLInputElement).value as ThemeDirection,
            }));
        },
        [setSettings]
    );

    const onChangeDirectionByLang = useCallback(
        (lang: string) => {
            setSettings((prev) => ({
                ...prev,
                themeDirection: lang === 'ar' ? 'rtl' : 'ltr',
            }));
        },
        [setSettings]
    );

    // Layout

    const onToggleLayout = useCallback(() => {
        setSettings((prev) => ({
            ...prev,
            themeLayout: prev.themeLayout === 'vertical' ? 'horizontal' : 'vertical',
        }));
    }, [setSettings]);

    const onChangeLayout = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSettings((prev) => ({
                ...prev,
                themeLayout: (event.target as HTMLInputElement).value as ThemeLayout,
            }));
        },
        [setSettings]
    );

    // Contrast

    const onToggleContrast = useCallback(() => {
        setSettings((prev) => ({
            ...prev,
            themeContrast: prev.themeContrast === 'default' ? 'bold' : 'default',
        }));
    }, [setSettings]);

    const onChangeContrast = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSettings((prev) => ({
                ...prev,
                themeContrast: (event.target as HTMLInputElement).value as ThemeContrast,
            }));
        },
        [setSettings]
    );

    // Color

    const onChangeColor = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSettings((prev) => ({
                ...prev,
                themeColorPresets: (event.target as HTMLInputElement).value as ThemeColorPresets,
            }));
        },
        [setSettings]
    );

    // Stretch

    const onToggleStretch = useCallback(() => {
        setSettings((prev) => ({
            ...prev,
            themeStretch: !prev.themeStretch,
        }));
    }, [setSettings]);

    // Reset

    const onResetSetting = useCallback(() => {
        setSettings({
            themeMode: initialState.themeMode,
            themeLayout: initialState.themeLayout,
            themeStretch: initialState.themeStretch,
            themeContrast: initialState.themeContrast,
            themeDirection: initialState.themeDirection,
            themeColorPresets: initialState.themeColorPresets,
        });
    }, [setSettings]);

    const contextValue = useMemo(
        () => ({
            ...settings,

            // Mode
            onToggleMode,
            onChangeMode,

            // Direction
            onToggleDirection,
            onChangeDirection,
            onChangeDirectionByLang,

            // Layout
            onToggleLayout,
            onChangeLayout,

            // Contrast
            onChangeContrast,
            onToggleContrast,

            // Stretch
            onToggleStretch,

            // Color
            onChangeColor,
            setColor: getColorPresets(settings.themeColorPresets),
            colorOption: colorPresets.map((color) => ({
                name: color.name,
                value: color.main,
            })),

            // Reset
            onResetSetting,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [settings]
    );

    return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
}

export { SettingsProvider, SettingsContext };
